import { AfterRenderRef, ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, ElementRef, EventEmitter, Injector, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild, afterNextRender, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { DataService } from '@app/core/services/data.service';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { Observable, Subject, timer } from 'rxjs';
import { debounceTime, takeUntil, finalize } from 'rxjs/operators';

type EditorOptions = {
    theme: string;
    language: string;
    automaticLayout: boolean;
    minimap: { enabled: boolean };
    fontSize: number;
    lineNumbers: string;
    scrollBeyondLastLine: boolean;
    readOnly?: boolean;
    domReadOnly?: boolean;
    wordWrap?: string;
    folding?: boolean;
    contextmenu?: boolean;
    quickSuggestions?: boolean;
    suggestOnTriggerCharacters?: boolean;
    tabCompletion?: string;
    fixedOverflowWidgets?: boolean;
    glyphMargin?: boolean;
    lineDecorationsWidth?: number;
    lineNumbersMinChars?: number;
};

interface ExpressionPreviewResponse {
    success: boolean;
    result?: number;
    errorMessage?: string;
}

interface ExpressionTemplate {
    label: string;
    description: string;
    expression: string;
    complexity: 'basic' | 'intermediate' | 'advanced';
    defaultVariables?: Record<string, number>;
}

interface VariableEntry {
    key: string;
    value: number;
}

export interface ExpressionCompletionColumn {
    label: string;
    field?: string;
    alias?: string;
    detail?: string;
    documentation?: string;
}

export type ExpressionCompletionColumnInput = string | ExpressionCompletionColumn;

interface ExpressionCompletionItem {
    label: string;
    insertText?: string;
    detail?: string;
    documentation?: string;
    kind?: 'field' | 'function' | 'snippet' | 'keyword';
}

type ExpressionValidationState = 'idle' | 'valid' | 'invalid';

interface WidgetSuggestionPosition {
    left: number;
    top: number;
    width: number;
}

@Component({
    selector: 'app-expression-editor',
    templateUrl: './expression-editor.component.html',
    styleUrls: ['./expression-editor.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => ExpressionEditorComponent),
            multi: true
        }
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ExpressionEditorComponent implements ControlValueAccessor, OnInit, OnChanges, OnDestroy {
    @ViewChild('widgetExpressionInput') private widgetExpressionInput?: ElementRef<HTMLTextAreaElement>;

    @Input() label: string;
    @Input() hint: string;
    @Input() readonly = false;
    @Input() editorHeight = 240;
    @Input() valueType: number = 0; // 0: number, 1: string, 2: bool, etc.
    @Input() sampleValue: any = 10;
    @Input() compact = false;
    @Input() showSampleTools = true;
    @Input() showVariableTools = true;
    @Input() showLibrary = true;
    @Input() showCompactHints = true;
    @Input() enableServerPreview = true;
    @Input() expressionMode: 'property' | 'widget' = 'property';
    @Input() editorLanguage: string | null = null;
    @Input() completionColumns: ExpressionCompletionColumnInput[] = [];
    @Input() completionItems: ExpressionCompletionItem[] = [];
    @Input() showQuickToolbar = true;
    @Input() validationState: ExpressionValidationState = 'idle';
    @Input() validationMessage = '';
    @Input() serverPreviewEvaluator?: (expression: string) => Observable<ExpressionPreviewResponse>;
    @Output() sampleValueChange = new EventEmitter<any>();

    value = '';
    disabled = false;
    previewResult: number | null = null;
    previewError: string | null = null;
    previewLoading = false;
    previewDurationMs: number | null = null;
    variableInputs: VariableEntry[] = [{ key: '', value: 0 }];
    currentTab = 0;
    editorReady = false;
    editorLoadTimedOut = false;
    cursorLine = 1;
    cursorColumn = 1;
    editorExpanded = false;
    validationDetailsExpanded = false;
    widgetSuggestionsOpen = false;
    widgetSuggestionIndex = 0;
    visibleWidgetSuggestions: ExpressionCompletionItem[] = [];
    widgetSuggestionPosition: WidgetSuggestionPosition = { left: 12, top: 12, width: 420 };
    private previewTrigger$ = new Subject<void>();
    private destroy$ = new Subject<void>();
    private completionProviderDisposable: { dispose: () => void } | null = null;
    private monacoEditor: any;
    private syncEditorValueOnInit = false;
    private scheduledEditorLayoutRef?: AfterRenderRef;
    private widgetSuggestionQuery = '';
    private widgetSuggestionCloseTimer?: ReturnType<typeof setTimeout>;

    editorOptions: EditorOptions = {
        theme: 'vs-light',
        language: 'csharp',
        automaticLayout: true,
        minimap: { enabled: false },
        fontSize: 13,
        lineNumbers: 'off',
        scrollBeyondLastLine: false,
        wordWrap: 'on',
        folding: false,
        contextmenu: true,
        quickSuggestions: true,
        suggestOnTriggerCharacters: true,
        tabCompletion: 'on',
        fixedOverflowWidgets: true
    };

    private onChange: (value: string) => void = () => { };
    private onTouched: () => void = () => { };

    expressionTemplates: ExpressionTemplate[] = [
        {
            label: 'Clamp + Normalize',
            description: 'Limits value between 0-100 and maps to 0-1.',
            expression: '(Math.Min(Math.Max(value, 0), 100)) / 100',
            complexity: 'basic'
        },
        {
            label: 'Soft Cap',
            description: 'Values above 100 grow at half speed.',
            expression: 'value <= 100 ? value : 100 + (value - 100) * 0.5',
            complexity: 'intermediate'
        },
        {
            label: 'Logistic Curve',
            description: 'S-curve that stays between 0 and 1.',
            expression: '1 / (1 + Math.Exp(-0.1 * (value - 50)))',
            complexity: 'advanced'
        },
        {
            label: 'Variable Assignment',
            description: 'Define temporary variables for cleaner calculations.',
            expression: 'Math.Min(value * 1.15, 100)',
            complexity: 'intermediate',
            defaultVariables: {}
        },
        {
            label: 'Threshold Booster (variable)',
            description: 'Requires variable: threshold. Boosts when value exceeds it.',
            expression: 'value < threshold ? value * 0.9 : value * 1.25',
            complexity: 'intermediate',
            defaultVariables: { threshold: 60 }
        },
        {
            label: 'Multi-Step Calculation',
            description: 'Complex calculation with nested expressions.',
            expression: 'Math.Round((value / 100) * 1.5 * 100)',
            complexity: 'advanced',
            defaultVariables: {}
        },
        {
            label: 'Range Mapping',
            description: 'Maps measurements to discrete outputs (e.g., cm to liters).',
            expression: 'value <= 15 ? 45 : value <= 30 ? 60 : 75',
            complexity: 'basic'
        }
    ];

    readonly widgetToolbarFunctions: ExpressionCompletionItem[] = [
        { label: 'sum("Column")', insertText: 'sum("Column")', kind: 'function' },
        { label: 'avg("Column")', insertText: 'avg("Column")', kind: 'function' },
        { label: 'min("Column")', insertText: 'min("Column")', kind: 'function' },
        { label: 'max("Column")', insertText: 'max("Column")', kind: 'function' },
        { label: 'count()', insertText: 'count()', kind: 'function' },
        { label: 'round(value)', insertText: 'round(value)', kind: 'function' },
        { label: 'abs(value)', insertText: 'abs(value)', kind: 'function' },
        { label: 'coalesce(value, fallback)', insertText: 'coalesce(value, "Unknown")', kind: 'function' },
        { label: 'percent(value, total)', insertText: 'percent(value, total)', kind: 'function' },
        { label: 'contains(text, search)', insertText: 'contains(text, "search")', kind: 'function' },
        { label: 'startsWith(text, search)', insertText: 'startsWith(text, "search")', kind: 'function' }
    ];
    readonly widgetToolbarOperators = ['+', '-', '*', '/', '>', '>=', '<', '<=', '==', '!=', '&&', '||'];
    readonly widgetToolbarBrackets = ['()', '("Column")', '(value)', '["Column"]'];

    constructor(
        private readonly dataService: DataService,
        private readonly changeDetectorRef: ChangeDetectorRef,
        private readonly injector: Injector,
        private readonly destroyRef: DestroyRef
    ) { }

    ngOnInit(): void {
        this.configureEditorOptions();
        timer(1500)
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                if (!this.editorReady) {
                    this.editorLoadTimedOut = true;
                }
            });
        this.previewTrigger$.pipe(
            debounceTime(600),
            takeUntil(this.destroy$)
        ).subscribe(() => this.evaluateExpression());
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['readonly'] || changes['disabled'] || changes['compact'] || changes['editorLanguage'] || changes['expressionMode']) {
            this.configureEditorOptions();
        }
        if ((changes['completionColumns'] || changes['completionItems'] || changes['expressionMode']) && this.monacoEditor) {
            this.registerCompletionProvider();
        }
        if ((changes['completionColumns'] || changes['completionItems'] || changes['expressionMode']) && this.showWidgetExpressionEditor()) {
            this.refreshWidgetSuggestions(this.widgetSuggestionsOpen);
        }
        if (changes['valueType'] && !changes['valueType'].firstChange) {
            // ValueType değiştiğinde sample value'yu sıfırla
            this.updateSampleValueForType(changes['valueType'].currentValue);
        }
        if (changes['sampleValue'] && !changes['sampleValue'].firstChange) {
            this.sampleValue = changes['sampleValue'].currentValue;
        }
        if (changes['validationMessage'] && !changes['validationMessage'].firstChange) {
            this.validationDetailsExpanded = false;
        }
    }

    ngOnDestroy(): void {
        this.clearScheduledEditorLayout();
        this.clearWidgetSuggestionCloseTimer();
        this.disposeCompletionProvider();
        this.destroy$.next();
        this.destroy$.complete();
    }

    handleEditorInit(editor: any): void {
        this.monacoEditor = editor;
        this.editorReady = true;
        this.editorLoadTimedOut = false;
        this.syncMonacoEditorState({ syncValue: true });
        this.registerCompletionProvider();
        this.updateCursorPosition(editor.getPosition?.());
        editor.onDidChangeCursorPosition?.((event: any) => {
            this.updateCursorPosition(event?.position);
        });
    }

    private updateSampleValueForType(type: number): void {
        switch (type) {
            case 3: // Number
            case 4: // NumberSelection
            case 11: // Currency
                this.sampleValue = 10;
                break;
            case 5: // Boolean
                this.sampleValue = false;
                break;
            case 6: // DateTime
                this.sampleValue = new Date().toISOString().slice(0, 16);
                break;
            default: // String ve diğerleri
                this.sampleValue = '';
                break;
        }
    }

    writeValue(value: string | null): void {
        this.value = value ?? '';
        this.syncMonacoEditorState({ syncValue: true });
    }

    registerOnChange(fn: (value: string) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
        this.configureEditorOptions();
    }

    handleEditorChange(code: string): void {
        if (this.disabled || this.readonly) {
            return;
        }
        this.value = code ?? '';
        this.onChange(this.value);
        this.onTouched();
        this.queuePreview();
    }

    handleSampleValueInput(value: string): void {
        if (this.disabled || this.readonly) {
            return;
        }

        if (this.valueType === 5) {
            if (typeof value === 'boolean') {
                this.sampleValue = value;
            } else {
                const normalized = String(value).toLowerCase();
                this.sampleValue = normalized === 'true' || normalized === '1' || normalized === 'on';
            }
        } else if (this.valueType === 6) {
            this.sampleValue = value;
        } else {
            const parsed = Number(value);
            this.sampleValue = !Number.isNaN(parsed) ? parsed : value;
        }
        this.sampleValueChange.emit(this.sampleValue);
        this.queuePreview();
    }

    triggerManualPreview(): void {
        this.evaluateExpression();
    }

    private queuePreview(): void {
        if (!this.enableServerPreview || this.readonly || this.disabled) {
            return;
        }
        this.previewTrigger$.next();
    }

    private evaluateExpression(): void {
        if (!this.enableServerPreview || this.readonly || this.disabled) {
            return;
        }

        if (!this.value?.trim()) {
            this.previewResult = null;
            this.previewError = null;
            this.previewDurationMs = null;
            return;
        }

        const startedAt = performance.now();
        const preview$ = this.serverPreviewEvaluator
            ? this.serverPreviewEvaluator(this.value)
            : this.dataService.postV2<ExpressionPreviewResponse>('client-services/expression/preview', {
                expression: this.value,
                sampleValue: this.sampleValue,
                additionalVariables: this.buildVariableDictionary()
            });

        this.previewLoading = true;
        preview$
            .pipe(finalize(() => { this.previewLoading = false; }))
            .subscribe({
                next: (response) => {
                    this.previewDurationMs = performance.now() - startedAt;
                    if (response?.success) {
                        this.previewResult = response.result ?? null;
                        this.previewError = null;
                    } else {
                        this.previewResult = null;
                        this.previewError = response?.errorMessage ?? 'Expression could not be evaluated.';
                    }
                },
                error: (error) => {
                    this.previewDurationMs = performance.now() - startedAt;
                    this.previewResult = null;
                    this.previewError = error?.error?.errorMessage || error?.error?.message || 'Expression could not be evaluated.';
                }
            });
    }

    applyTemplate(template: ExpressionTemplate): void {
        if (this.disabled || this.readonly) {
            return;
        }

        this.value = template.expression;
        this.onChange(this.value);
        this.onTouched();

        if (template.defaultVariables) {
            this.variableInputs = Object.entries(template.defaultVariables)
                .map(([key, val]) => ({ key, value: val }));
        }

        this.queuePreview();
    }

    showQuickExpressionToolbar(): boolean {
        return this.showQuickToolbar && this.compact && this.expressionMode === 'widget';
    }

    showWidgetExpressionEditor(): boolean {
        return this.showQuickExpressionToolbar();
    }

    getToolbarColumns(): ExpressionCompletionItem[] {
        return this.getNormalizedCompletionColumns();
    }

    trackToolbarColumn(_index: number, column: ExpressionCompletionItem): string {
        return `${column.insertText || column.label || _index}`;
    }

    insertToolbarColumn(column: ExpressionCompletionItem): void {
        const insertText = String(column?.insertText || column?.label || '').trim();
        this.insertExpressionText(insertText);
    }

    insertExpressionText(text: string, options: { placeCursorInside?: boolean } = {}): void {
        if (this.disabled || this.readonly || !text) {
            return;
        }

        if (this.showWidgetExpressionEditor()) {
            this.insertTextInWidgetEditor(text, options);
            return;
        }

        if (this.monacoEditor) {
            const model = this.monacoEditor.getModel?.();
            const selection = this.monacoEditor.getSelection?.();
            const position = this.monacoEditor.getPosition?.();
            const range = selection || (position
                ? {
                    startLineNumber: position.lineNumber,
                    startColumn: position.column,
                    endLineNumber: position.lineNumber,
                    endColumn: position.column
                }
                : null);

            if (!range) {
                const separator = this.value && !this.value.endsWith(' ') ? ' ' : '';
                this.handleEditorChange(`${this.value || ''}${separator}${text}`);
                return;
            }

            this.monacoEditor.pushUndoStop?.();
            const editOperation = {
                range,
                text,
                forceMoveMarkers: true
            };
            const editsApplied = this.monacoEditor.executeEdits?.('quick-toolbar', [editOperation]);
            if (editsApplied === false) {
                model?.applyEdits?.([editOperation]);
            }
            this.monacoEditor.pushUndoStop?.();

            const startLine = range.startLineNumber ?? position?.lineNumber ?? 1;
            const startColumn = range.startColumn ?? position?.column ?? 1;
            if (options.placeCursorInside && text === '()') {
                this.monacoEditor.setPosition?.({
                    lineNumber: startLine,
                    column: startColumn + 1
                });
            } else {
                const insertedLines = text.split('\n');
                const lineNumber = startLine + insertedLines.length - 1;
                const column = insertedLines.length === 1
                    ? startColumn + text.length
                    : insertedLines[insertedLines.length - 1].length + 1;
                this.monacoEditor.setPosition?.({ lineNumber, column });
            }

            this.monacoEditor.focus?.();
            this.handleEditorChange(model?.getValue?.() ?? this.monacoEditor.getValue?.() ?? this.value);
            this.changeDetectorRef.markForCheck();
            return;
        }

        const separator = this.value && !this.value.endsWith(' ') ? ' ' : '';
        this.handleEditorChange(`${this.value || ''}${separator}${text}`);
    }

    triggerSuggest(): void {
        if (this.showWidgetExpressionEditor()) {
            this.openWidgetSuggestions(this.widgetExpressionInput?.nativeElement, true);
            return;
        }

        if (!this.monacoEditor) {
            return;
        }

        this.monacoEditor.focus?.();
        this.monacoEditor.trigger?.('quick-toolbar', 'editor.action.triggerSuggest', {});
    }

    handleWidgetEditorInput(textarea: HTMLTextAreaElement): void {
        this.handleEditorChange(textarea.value);
        this.updateCursorFromWidgetTextarea(textarea);
        this.openWidgetSuggestions(textarea, false);
    }

    handleWidgetEditorFocus(textarea: HTMLTextAreaElement): void {
        this.clearWidgetSuggestionCloseTimer();
        this.updateCursorFromWidgetTextarea(textarea);
        this.updateWidgetSuggestionPosition(textarea);
    }

    handleWidgetEditorBlur(): void {
        this.clearWidgetSuggestionCloseTimer();
        this.widgetSuggestionCloseTimer = setTimeout(() => {
            this.widgetSuggestionsOpen = false;
            this.changeDetectorRef.markForCheck();
        }, 140);
    }

    handleWidgetEditorScroll(textarea: HTMLTextAreaElement): void {
        if (!this.widgetSuggestionsOpen) {
            return;
        }

        this.updateWidgetSuggestionPosition(textarea);
    }

    handleWidgetEditorKeydown(event: KeyboardEvent, textarea: HTMLTextAreaElement): void {
        if ((event.ctrlKey || event.metaKey) && event.code === 'Space') {
            event.preventDefault();
            this.openWidgetSuggestions(textarea, true);
            return;
        }

        if (!this.widgetSuggestionsOpen) {
            return;
        }

        if (event.key === 'ArrowDown') {
            event.preventDefault();
            this.moveWidgetSuggestion(1);
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            this.moveWidgetSuggestion(-1);
        } else if (event.key === 'Enter' || event.key === 'Tab') {
            const suggestion = this.visibleWidgetSuggestions[this.widgetSuggestionIndex];
            if (suggestion) {
                event.preventDefault();
                this.applyWidgetSuggestion(suggestion);
            }
        } else if (event.key === 'Escape') {
            event.preventDefault();
            this.widgetSuggestionsOpen = false;
            this.changeDetectorRef.markForCheck();
        }
    }

    applyWidgetSuggestion(suggestion: ExpressionCompletionItem): void {
        const insertText = suggestion.insertText || suggestion.label;
        this.replaceWidgetTokenInEditor(insertText);
    }

    trackWidgetSuggestion(_index: number, suggestion: ExpressionCompletionItem): string {
        return `${suggestion.kind || 'item'}:${suggestion.insertText || suggestion.label || _index}`;
    }

    toggleEditorExpanded(): void {
        this.editorExpanded = !this.editorExpanded;
        this.scheduleEditorLayout();
    }

    getEditorStatusState(): ExpressionValidationState {
        if (this.enableServerPreview) {
            if (this.previewError) {
                return 'invalid';
            }
            if (this.previewResult !== null) {
                return 'valid';
            }
        }

        return this.validationState || 'idle';
    }

    getEditorStatusLabel(): string {
        if (this.previewLoading) {
            return 'SystemAdmin.Components.ExpressionEditor.Status.Running';
        }

        if (this.validationMessage?.trim()) {
            return this.validationMessage;
        }

        switch (this.getEditorStatusState()) {
            case 'valid':
                return 'SystemAdmin.Components.ExpressionEditor.Status.Valid';
            case 'invalid':
                return 'SystemAdmin.Components.ExpressionEditor.Status.Invalid';
            default:
                return 'SystemAdmin.Components.ExpressionEditor.Status.Ready';
        }
    }

    getValidationMessageSummary(): string {
        const lines = this.getValidationMessageLines();
        return lines[0] || '';
    }

    getValidationMessageDetails(): string {
        return this.getValidationMessageLines().slice(1).join('\n');
    }

    hasValidationMessageDetails(): boolean {
        return this.getValidationMessageLines().length > 1;
    }

    toggleValidationDetails(event: Event): void {
        event.preventDefault();
        event.stopPropagation();
        this.validationDetailsExpanded = !this.validationDetailsExpanded;
    }

    private getValidationMessageLines(): string[] {
        return String(this.validationMessage || '')
            .replace(/\r\n/g, '\n')
            .split('\n')
            .map((line) => line.trimEnd())
            .filter((line) => line.trim().length > 0);
    }

    getEditorStatusIcon(): IconProp {
        switch (this.getEditorStatusState()) {
            case 'valid':
                return ['fal', 'circle-check'] as IconProp;
            case 'invalid':
                return ['fal', 'circle-exclamation'] as IconProp;
            default:
                return ['fal', 'circle'] as IconProp;
        }
    }

    getEditorExpandIcon(): IconProp {
        return (this.editorExpanded
            ? ['fal', 'down-left-and-up-right-to-center']
            : ['fal', 'up-right-and-down-left-from-center']) as IconProp;
    }

    private updateCursorPosition(position: { lineNumber: number; column: number } | null | undefined): void {
        this.cursorLine = position?.lineNumber || 1;
        this.cursorColumn = position?.column || 1;
        this.changeDetectorRef.markForCheck();
    }

    private insertTextInWidgetEditor(text: string, options: { placeCursorInside?: boolean } = {}): void {
        const textarea = this.widgetExpressionInput?.nativeElement;
        if (!textarea) {
            const separator = this.value && !this.value.endsWith(' ') ? ' ' : '';
            this.handleEditorChange(`${this.value || ''}${separator}${text}`);
            this.widgetSuggestionsOpen = false;
            return;
        }

        const currentValue = textarea.value ?? this.value ?? '';
        const start = textarea.selectionStart ?? currentValue.length;
        const end = textarea.selectionEnd ?? start;
        const nextValue = `${currentValue.slice(0, start)}${text}${currentValue.slice(end)}`;
        const nextCursor = start + (options.placeCursorInside && text === '()' ? 1 : text.length);

        textarea.value = nextValue;
        this.handleEditorChange(nextValue);
        textarea.focus();
        textarea.setSelectionRange(nextCursor, nextCursor);
        this.updateCursorFromWidgetTextarea(textarea);
        this.widgetSuggestionsOpen = false;
        this.changeDetectorRef.markForCheck();
    }

    private replaceWidgetTokenInEditor(text: string): void {
        const textarea = this.widgetExpressionInput?.nativeElement;
        if (!textarea) {
            this.insertTextInWidgetEditor(text);
            return;
        }

        const currentValue = textarea.value ?? this.value ?? '';
        const cursor = textarea.selectionStart ?? currentValue.length;
        const range = this.getWidgetTokenRange(textarea);
        const start = range?.start ?? cursor;
        const end = range?.end ?? cursor;
        const nextValue = `${currentValue.slice(0, start)}${text}${currentValue.slice(end)}`;
        const nextCursor = start + text.length;

        textarea.value = nextValue;
        this.handleEditorChange(nextValue);
        textarea.focus();
        textarea.setSelectionRange(nextCursor, nextCursor);
        this.updateCursorFromWidgetTextarea(textarea);
        this.widgetSuggestionsOpen = false;
        this.changeDetectorRef.markForCheck();
    }

    private openWidgetSuggestions(textarea: HTMLTextAreaElement | undefined, force: boolean): void {
        if (this.disabled || this.readonly) {
            this.widgetSuggestionsOpen = false;
            return;
        }

        this.clearWidgetSuggestionCloseTimer();
        if (textarea) {
            this.updateCursorFromWidgetTextarea(textarea);
            this.updateWidgetSuggestionQuery(textarea);
        } else {
            this.widgetSuggestionQuery = '';
        }

        this.refreshWidgetSuggestions(force);
        this.widgetSuggestionsOpen = force || this.widgetSuggestionQuery.length > 0;
        if (this.widgetSuggestionsOpen && textarea) {
            this.updateWidgetSuggestionPosition(textarea);
        }
        if (!this.visibleWidgetSuggestions.length) {
            this.widgetSuggestionIndex = 0;
        }
        this.changeDetectorRef.markForCheck();
    }

    private refreshWidgetSuggestions(showAllWhenQueryEmpty = false): void {
        const query = this.widgetSuggestionQuery.trim().toLocaleLowerCase('en-US');
        const suggestions = this.getWidgetExpressionSuggestionItems();
        const filtered = !query && showAllWhenQueryEmpty
            ? suggestions
            : suggestions.filter((item) => {
                const haystack = [
                    item.label,
                    item.insertText,
                    item.detail,
                    item.documentation
                ].filter(Boolean).join(' ').toLocaleLowerCase('en-US');
                return query.length > 0 && haystack.includes(query);
            });

        this.visibleWidgetSuggestions = filtered.slice(0, 12);
        this.widgetSuggestionIndex = Math.min(
            this.widgetSuggestionIndex,
            Math.max(this.visibleWidgetSuggestions.length - 1, 0)
        );
    }

    private getWidgetExpressionSuggestionItems(): ExpressionCompletionItem[] {
        const items: ExpressionCompletionItem[] = [
            ...this.buildWidgetExpressionCompletions(),
            ...this.widgetToolbarOperators.map(operator => ({
                label: operator,
                insertText: operator,
                detail: 'Operator',
                kind: 'keyword' as const
            })),
            ...this.widgetToolbarBrackets.map(bracket => ({
                label: bracket,
                insertText: bracket,
                detail: 'Grouping',
                kind: 'snippet' as const
            }))
        ];

        const seen = new Set<string>();
        return items.filter((item) => {
            const key = `${item.kind || 'item'}:${item.label}:${item.insertText || ''}`.toLocaleLowerCase('en-US');
            if (seen.has(key)) {
                return false;
            }
            seen.add(key);
            return true;
        });
    }

    private moveWidgetSuggestion(direction: 1 | -1): void {
        const total = this.visibleWidgetSuggestions.length;
        if (!total) {
            this.widgetSuggestionIndex = 0;
            return;
        }

        this.widgetSuggestionIndex = (this.widgetSuggestionIndex + direction + total) % total;
        this.changeDetectorRef.markForCheck();
    }

    private updateWidgetSuggestionQuery(textarea: HTMLTextAreaElement): void {
        const range = this.getWidgetTokenRange(textarea);
        this.widgetSuggestionQuery = range?.query ?? '';
        this.widgetSuggestionIndex = 0;
    }

    private getWidgetTokenRange(textarea: HTMLTextAreaElement): { start: number; end: number; query: string } | null {
        const cursor = textarea.selectionStart ?? textarea.value.length;
        const beforeCursor = textarea.value.slice(0, cursor);
        const match = beforeCursor.match(/[\p{L}\p{Nd}_."']+$/u);
        const token = match?.[0] ?? '';
        const query = token
            .replace(/^["'.]+/, '')
            .replace(/["']+$/, '')
            .trim();

        if (!query) {
            return null;
        }

        const leadingIgnored = token.match(/^["'.]+/)?.[0]?.length ?? 0;
        return {
            start: cursor - token.length + leadingIgnored,
            end: cursor,
            query
        };
    }

    private updateCursorFromWidgetTextarea(textarea: HTMLTextAreaElement): void {
        const cursor = textarea.selectionStart ?? textarea.value.length;
        const beforeCursor = textarea.value.slice(0, cursor);
        const lines = beforeCursor.split('\n');
        this.cursorLine = lines.length;
        this.cursorColumn = (lines[lines.length - 1]?.length ?? 0) + 1;
        this.changeDetectorRef.markForCheck();
    }

    private updateWidgetSuggestionPosition(textarea: HTMLTextAreaElement): void {
        const frame = textarea.parentElement;
        if (!frame) {
            return;
        }

        const cursor = textarea.selectionStart ?? textarea.value.length;
        const caret = this.getTextareaCaretCoordinates(textarea, cursor);
        const lineHeight = this.getTextareaLineHeight(textarea);
        const frameRect = frame.getBoundingClientRect();
        const textareaRect = textarea.getBoundingClientRect();
        const margin = 8;
        const availableWidth = Math.max((frame.clientWidth || textarea.clientWidth) - margin * 2, 0);
        const width = Math.min(availableWidth, Math.max(Math.min(360, availableWidth), Math.min(672, availableWidth * 0.42)));
        const rawLeft = textareaRect.left - frameRect.left + caret.left - textarea.scrollLeft;
        const rawTop = textareaRect.top - frameRect.top + caret.top - textarea.scrollTop + lineHeight + 4;
        const maxLeft = Math.max(margin, (frame.clientWidth || textarea.clientWidth) - width - margin);

        this.widgetSuggestionPosition = {
            left: Math.round(Math.min(Math.max(rawLeft, margin), maxLeft)),
            top: Math.round(Math.max(rawTop, margin)),
            width: Math.round(width)
        };
    }

    private getTextareaCaretCoordinates(textarea: HTMLTextAreaElement, position: number): { left: number; top: number } {
        const computedStyle = window.getComputedStyle(textarea);
        const mirror = document.createElement('div');
        const mirrorStyleProperties = [
            'box-sizing',
            'width',
            'border-top-width',
            'border-right-width',
            'border-bottom-width',
            'border-left-width',
            'padding-top',
            'padding-right',
            'padding-bottom',
            'padding-left',
            'font-family',
            'font-size',
            'font-style',
            'font-variant',
            'font-weight',
            'letter-spacing',
            'line-height',
            'text-align',
            'text-indent',
            'text-transform',
            'tab-size'
        ];

        for (const property of mirrorStyleProperties) {
            mirror.style.setProperty(property, computedStyle.getPropertyValue(property));
        }

        mirror.style.position = 'absolute';
        mirror.style.visibility = 'hidden';
        mirror.style.whiteSpace = 'pre-wrap';
        mirror.style.wordBreak = 'break-word';
        mirror.style.overflowWrap = 'break-word';
        mirror.style.overflow = 'hidden';
        mirror.style.left = '-9999px';
        mirror.style.top = '0';
        mirror.style.height = 'auto';
        mirror.style.minHeight = '0';
        mirror.style.maxHeight = 'none';
        mirror.style.width = `${textarea.clientWidth}px`;

        const marker = document.createElement('span');
        marker.textContent = '\u200b';
        mirror.textContent = textarea.value.slice(0, position);
        mirror.appendChild(marker);
        document.body.appendChild(mirror);

        const coordinates = {
            left: marker.offsetLeft,
            top: marker.offsetTop
        };

        mirror.remove();
        return coordinates;
    }

    private getTextareaLineHeight(textarea: HTMLTextAreaElement): number {
        const computedStyle = window.getComputedStyle(textarea);
        const lineHeight = parseFloat(computedStyle.lineHeight);
        if (Number.isFinite(lineHeight)) {
            return lineHeight;
        }

        const fontSize = parseFloat(computedStyle.fontSize);
        return Number.isFinite(fontSize) ? fontSize * 1.55 : 20;
    }

    private clearWidgetSuggestionCloseTimer(): void {
        if (!this.widgetSuggestionCloseTimer) {
            return;
        }

        clearTimeout(this.widgetSuggestionCloseTimer);
        this.widgetSuggestionCloseTimer = undefined;
    }

    addVariableRow(): void {
        this.variableInputs = [...this.variableInputs, { key: '', value: 0 }];
    }

    removeVariableRow(index: number): void {
        if (this.variableInputs.length === 1) {
            this.variableInputs = [{ key: '', value: 0 }];
            return;
        }
        this.variableInputs = this.variableInputs.filter((_, i) => i !== index);
        this.queuePreview();
    }

    updateVariableKey(index: number, key: string): void {
        const next = [...this.variableInputs];
        next[index] = { ...next[index], key };
        this.variableInputs = next;
    }

    updateVariableValue(index: number, value: string): void {
        const parsed = Number(value);
        const next = [...this.variableInputs];
        next[index] = { ...next[index], value: Number.isNaN(parsed) ? 0 : parsed };
        this.variableInputs = next;
        this.queuePreview();
    }

    private buildVariableDictionary(): Record<string, number> {
        return this.variableInputs
            .filter(v => v.key?.trim()?.length)
            .reduce((acc, curr) => {
                acc[curr.key.trim()] = curr.value;
                return acc;
            }, {} as Record<string, number>);
    }

    private configureEditorOptions(): void {
        const language = this.resolveEditorLanguage();
        this.editorOptions = {
            ...this.editorOptions,
            language,
            readOnly: this.readonly || this.disabled,
            domReadOnly: this.readonly || this.disabled,
            fontSize: this.showQuickExpressionToolbar() ? 13 : (this.compact ? 12 : 13),
            lineNumbers: this.showQuickExpressionToolbar() ? 'on' : (this.compact ? 'off' : this.editorOptions.lineNumbers),
            glyphMargin: false,
            lineDecorationsWidth: this.showQuickExpressionToolbar() ? 10 : (this.compact ? 8 : 10),
            lineNumbersMinChars: this.showQuickExpressionToolbar() ? 2 : (this.compact ? 0 : 3)
        };

        this.syncMonacoEditorState();
    }

    private resolveEditorLanguage(): string {
        if (this.expressionMode === 'widget') {
            const requestedLanguage = this.editorLanguage?.trim();
            return requestedLanguage && requestedLanguage !== 'javascript'
                ? requestedLanguage
                : 'widget-expression';
        }

        return this.editorLanguage || 'csharp';
    }

    private syncMonacoEditorState(options: { syncValue?: boolean } = {}): void {
        if (!this.monacoEditor) {
            this.syncEditorValueOnInit = !!options.syncValue;
            return;
        }

        const isReadOnly = this.readonly || this.disabled;
        const monacoApi = (window as any)?.monaco;
        this.syncMonacoModelLanguage(monacoApi, this.editorOptions.language || 'csharp');
        this.monacoEditor.updateOptions?.({
            readOnly: isReadOnly,
            domReadOnly: isReadOnly
        });

        if (options.syncValue || this.syncEditorValueOnInit) {
            this.syncEditorValueOnInit = false;
            const currentValue = this.monacoEditor.getValue?.() ?? '';
            const nextValue = this.value ?? '';
            if (currentValue !== nextValue) {
                this.monacoEditor.setValue?.(nextValue);
            }
        }

        this.scheduleEditorLayout();
    }

    private scheduleEditorLayout(): void {
        this.clearScheduledEditorLayout();
        this.scheduledEditorLayoutRef = afterNextRender({
            write: () => {
                this.scheduledEditorLayoutRef = undefined;
                if (!this.destroyRef.destroyed) {
                    this.monacoEditor?.layout?.();
                }
            }
        }, { injector: this.injector });
    }

    private clearScheduledEditorLayout(): void {
        if (!this.scheduledEditorLayoutRef) {
            return;
        }

        this.scheduledEditorLayoutRef.destroy();
        this.scheduledEditorLayoutRef = undefined;
    }

    private registerCompletionProvider(): void {
        const monacoApi = (window as any)?.monaco;
        if (!monacoApi?.languages) {
            return;
        }

        this.disposeCompletionProvider();
        const language = this.editorOptions.language || 'csharp';
        if (!this.isLanguageRegistered(monacoApi, language)) {
            monacoApi.languages.register({ id: language });
        }
        this.syncMonacoModelLanguage(monacoApi, language);

        this.completionProviderDisposable = monacoApi.languages.registerCompletionItemProvider(language, {
            triggerCharacters: ['"', '.', '(', ','],
            provideCompletionItems: (model: any, position: any) => {
                const word = model?.getWordUntilPosition?.(position);
                const range = word
                    ? new monacoApi.Range(position.lineNumber, word.startColumn, position.lineNumber, word.endColumn)
                    : new monacoApi.Range(position.lineNumber, position.column, position.lineNumber, position.column);

                return {
                    suggestions: this.buildCompletionSuggestions(monacoApi, range)
                };
            }
        });
    }

    private disposeCompletionProvider(): void {
        if (!this.completionProviderDisposable) {
            return;
        }
        this.completionProviderDisposable.dispose();
        this.completionProviderDisposable = null;
    }

    private isLanguageRegistered(monacoApi: any, language: string): boolean {
        return monacoApi.languages.getLanguages()
            .some((item: any) => item?.id === language);
    }

    private buildCompletionSuggestions(monacoApi: any, range: any): any[] {
        const suggestions: any[] = [];
        const keywordKind = monacoApi.languages.CompletionItemKind.Keyword;
        const fieldKind = monacoApi.languages.CompletionItemKind.Field;
        const functionKind = monacoApi.languages.CompletionItemKind.Function;
        const snippetKind = monacoApi.languages.CompletionItemKind.Snippet;

        if (this.expressionMode === 'widget') {
            for (const item of this.buildWidgetExpressionCompletions()) {
                suggestions.push(this.toMonacoCompletion(monacoApi, item, {
                    field: fieldKind,
                    function: functionKind,
                    snippet: snippetKind,
                    keyword: keywordKind
                }, range));
            }
        } else {
            suggestions.push(
                this.toMonacoCompletion(monacoApi, {
                    label: 'value',
                    insertText: 'value',
                    detail: 'Current property value',
                    kind: 'field'
                }, { field: fieldKind, function: functionKind, snippet: snippetKind, keyword: keywordKind }, range),
                this.toMonacoCompletion(monacoApi, {
                    label: 'Math.Round(value)',
                    insertText: 'Math.Round(value)',
                    detail: 'Round a numeric value',
                    kind: 'function'
                }, { field: fieldKind, function: functionKind, snippet: snippetKind, keyword: keywordKind }, range),
                this.toMonacoCompletion(monacoApi, {
                    label: 'Math.Min(value, 100)',
                    insertText: 'Math.Min(value, 100)',
                    detail: 'Clamp to an upper limit',
                    kind: 'function'
                }, { field: fieldKind, function: functionKind, snippet: snippetKind, keyword: keywordKind }, range),
                this.toMonacoCompletion(monacoApi, {
                    label: 'value <= 5 ? 0 : value',
                    insertText: 'value <= 5 ? 0 : value',
                    detail: 'Ternary condition',
                    kind: 'snippet'
                }, { field: fieldKind, function: functionKind, snippet: snippetKind, keyword: keywordKind }, range)
            );
        }

        for (const item of this.completionItems || []) {
            suggestions.push(this.toMonacoCompletion(monacoApi, item, {
                field: fieldKind,
                function: functionKind,
                snippet: snippetKind,
                keyword: keywordKind
            }, range));
        }

        return suggestions;
    }

    private buildWidgetExpressionCompletions(): ExpressionCompletionItem[] {
        const columns = this.getNormalizedCompletionColumns();
        const completions: ExpressionCompletionItem[] = [];

        for (const column of columns) {
            completions.push({
                label: column.label || column.insertText,
                insertText: column.insertText || column.label,
                detail: column.detail || column.label,
                documentation: column.documentation || 'Use this field in row or style calculations.',
                kind: 'field'
            });
        }

        const aggregateColumn = this.getFirstAggregateColumnName(columns);
        const aggregateSnippets: ExpressionCompletionItem[] = [
            { label: 'sum("Column")', insertText: `sum("${aggregateColumn}")`, detail: 'Sum values for a report column', kind: 'function' },
            { label: 'avg("Column")', insertText: `avg("${aggregateColumn}")`, detail: 'Average values for a report column', kind: 'function' },
            { label: 'min("Column")', insertText: `min("${aggregateColumn}")`, detail: 'Minimum value for a report column', kind: 'function' },
            { label: 'max("Column")', insertText: `max("${aggregateColumn}")`, detail: 'Maximum value for a report column', kind: 'function' },
            { label: 'count()', insertText: 'count()', detail: 'Number of preview rows', kind: 'function' },
            { label: 'round(value)', insertText: 'round(value)', detail: 'Round a numeric value', kind: 'function' },
            { label: 'abs(value)', insertText: 'abs(value)', detail: 'Absolute value', kind: 'function' },
            { label: 'coalesce(value, fallback)', insertText: 'coalesce(value, "Unknown")', detail: 'Return fallback when value is empty/null', kind: 'function' },
            { label: 'percent(value, total)', insertText: 'percent(value, total)', detail: 'Calculate percentage', kind: 'function' },
            { label: 'contains(text, search)', insertText: 'contains(text, "search")', detail: 'Case-insensitive contains check', kind: 'function' },
            { label: 'startsWith(text, search)', insertText: 'startsWith(text, "search")', detail: 'Case-insensitive prefix check', kind: 'function' }
        ];

        return [...completions, ...aggregateSnippets];
    }

    private getNormalizedCompletionColumns(): ExpressionCompletionItem[] {
        const columns: ExpressionCompletionItem[] = [];
        const usedAliases = new Set<string>();

        for (const source of this.completionColumns || []) {
            const normalized = this.normalizeCompletionColumnInput(source);
            if (!normalized) {
                continue;
            }

            const baseAlias = normalized.alias || this.normalizeExpressionIdentifier(normalized.field || normalized.label);
            let alias = baseAlias;
            if (!normalized.alias) {
                let suffix = 2;
                while (usedAliases.has(alias.toLocaleLowerCase('en-US'))) {
                    alias = `${baseAlias}${suffix}`;
                    suffix++;
                }
            }
            usedAliases.add(alias.toLocaleLowerCase('en-US'));

            const field = normalized.field || normalized.label;
            const detail = normalized.detail || (field && field !== alias ? alias : field);
            columns.push({
                label: normalized.label || field || alias,
                insertText: alias,
                detail,
                documentation: normalized.documentation || (field ? `Field: ${field}` : undefined),
                kind: 'field'
            });
        }

        return columns;
    }

    private normalizeCompletionColumnInput(source: ExpressionCompletionColumnInput): ExpressionCompletionColumn | null {
        if (typeof source === 'string') {
            const field = source.trim();
            return field ? { label: field, field } : null;
        }

        const label = String(source?.label || source?.field || source?.alias || '').trim();
        const field = String(source?.field || label).trim();
        const alias = String(source?.alias || '').trim();
        if (!label && !field && !alias) {
            return null;
        }

        return {
            label: label || field || alias,
            field: field || label || alias,
            alias: alias || undefined,
            detail: source?.detail,
            documentation: source?.documentation
        };
    }

    private getFirstAggregateColumnName(columns: ExpressionCompletionItem[]): string {
        const firstColumn = (this.completionColumns || []).find((source) => {
            if (typeof source === 'string') {
                return source.trim().length > 0;
            }

            return !!String(source?.field || source?.label || '').trim();
        });

        if (typeof firstColumn === 'string') {
            return firstColumn.trim() || 'Column';
        }

        return String(firstColumn?.field || firstColumn?.label || columns[0]?.label || 'Column').trim() || 'Column';
    }

    private toMonacoCompletion(monacoApi: any, item: ExpressionCompletionItem, kinds: Record<string, any>, range: any): any {
        return {
            label: item.label,
            insertText: item.insertText || item.label,
            kind: kinds[item.kind || 'keyword'] || kinds.keyword,
            range,
            detail: item.detail,
            documentation: item.documentation,
            insertTextRules: item.kind === 'snippet'
                ? monacoApi.languages.CompletionItemInsertTextRule.InsertAsSnippet
                : undefined
        };
    }

    private normalizeExpressionIdentifier(value: string): string {
        if (!value?.trim()) {
            return 'value';
        }

        const withoutMarks = value.normalize('NFD').replace(/\p{Mark}/gu, '');
        let safe = withoutMarks.replace(/[^\p{L}\p{Nd}_]+/gu, '_').replace(/^_+|_+$/g, '');
        if (!safe) {
            safe = 'value';
        }

        safe = safe.charAt(0).toLocaleLowerCase('en-US') + safe.slice(1);
        if (/^\p{Nd}/u.test(safe)) {
            safe = `c_${safe}`;
        }

        return safe;
    }

    private syncMonacoModelLanguage(monacoApi: any, language: string): void {
        if (!this.monacoEditor || !monacoApi?.editor?.setModelLanguage || !language) {
            return;
        }

        const model = this.monacoEditor.getModel?.();
        if (!model) {
            return;
        }

        const currentLanguage = model.getLanguageId?.();
        if (currentLanguage !== language) {
            monacoApi.editor.setModelLanguage(model, language);
        }
    }
}
